/** @format */

import { api } from "./client";
import type { SopListFilters } from "../store/useSopStore";

export interface SopWithMultipleVersionsItem {
  sopId: string;
  title: string;
  organizationName: string | null;
  createdAt: string;
  createdBy: string | null;
  versionCount: number;
}

export interface SopsWithMultipleVersionsPagination {
  page: number;
  limit: number;
  total: number;
  hasNext: boolean;
}

export interface SopsWithMultipleVersionsResponse {
  sops: SopWithMultipleVersionsItem[];
  pagination: SopsWithMultipleVersionsPagination;
}

const DEFAULT_LIMIT = 20;
const MAX_LIMIT = 100;

/**
 * GET /sops/with-multiple-versions – list SOPs that have more than one version (for change tracking).
 * Uses same auth as other SOP endpoints (Bearer + X-Organization-Id when set); backend enforces org scope (admin sees all).
 */
export async function getSopsWithMultipleVersions(options?: {
  page?: number;
  limit?: number;
} & SopListFilters): Promise<SopsWithMultipleVersionsResponse> {
  const page = Math.max(1, options?.page ?? 1);
  const limit = Math.min(
    MAX_LIMIT,
    Math.max(1, options?.limit ?? DEFAULT_LIMIT),
  );
  const params: Record<string, string | number> = { page, limit };
  const dateFrom = options?.dateFrom?.trim();
  const dateTo = options?.dateTo?.trim();
  const sopTitle = options?.sopTitle?.trim();
  const organizationId = options?.organizationId?.trim();
  const creatorEmail = options?.creatorEmail?.trim();
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (sopTitle) params.sopTitle = sopTitle;
  if (organizationId) params.organizationId = organizationId;
  if (creatorEmail) params.creatorEmail = creatorEmail;

  const response = await api.get<SopsWithMultipleVersionsResponse>(
    "/sops/with-multiple-versions",
    { params },
  );
  return response.data;
}
